// Auto-add free-gift engine.
// Watches the cart and keeps a free gift line in sync with offer eligibility.
// Offers are injected by snippets/auto-add-offer.liquid as window.__autoAddOffers.
(function () {
  'use strict';

  var DEBOUNCE_MS = 150;

  window.AutoAddOffer = {
    offers: [],
    inFlight: {},      // offerId -> bool (add/remove request in progress)
    _lastGiftPresent: {}, // offerId -> bool (gift present at end of previous evaluation)
    _debounce: null,

    init: function () {
      this.offers = Array.isArray(window.__autoAddOffers) ? window.__autoAddOffers : [];
      if (!this.offers.length) return;

      var self = this;
      document.addEventListener('cart:updated', function (e) {
        var cart = e.detail && e.detail.cart ? e.detail.cart : null;
        self.scheduleEvaluate(cart);
      });

      this.wireReminderButtons();

      // Initial pass needs current cart state.
      this.fetchCart().then(function (cart) { self.evaluate(cart); });
    },

    fetchCart: function () {
      return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); });
    },

    scheduleEvaluate: function (cart) {
      var self = this;
      clearTimeout(this._debounce);
      this._debounce = setTimeout(function () {
        if (cart) { self.evaluate(cart); }
        else { self.fetchCart().then(function (c) { self.evaluate(c); }); }
      }, DEBOUNCE_MS);
    },

    getGiftLine: function (offer, cart) {
      var items = (cart && cart.items) || [];
      for (var i = 0; i < items.length; i++) {
        if (items[i].variant_id === offer.freeVariantId) return items[i];
      }
      return null;
    },

    getAutoGiftLine: function (offer, cart) {
      var items = (cart && cart.items) || [];
      for (var i = 0; i < items.length; i++) {
        if (items[i].variant_id === offer.freeVariantId &&
            items[i].properties && items[i].properties._auto_added === 'true') {
          return items[i];
        }
      }
      return null;
    },

    isEligible: function (offer, cart) {
      var items = (cart && cart.items) || [];
      if (offer.triggerType === 'Collection in Cart') {
        var ids = offer.eligibleProductIds || [];
        var qty = 0;
        for (var i = 0; i < items.length; i++) {
          if (ids.indexOf(items[i].product_id) !== -1) qty += items[i].quantity;
        }
        return qty >= (offer.requiredQuantity || 1);
      }
      if (offer.triggerType === 'Spend Threshold') {
        if (offer.spendThreshold == null) return false; // currency mismatch or unset
        var giftLine = this.getGiftLine(offer, cart);
        var giftContribution = giftLine ? giftLine.final_line_price : 0;
        var nonGiftSubtotal = (cart.items_subtotal_price || 0) - giftContribution;
        return nonGiftSubtotal >= offer.spendThreshold;
      }
      return false;
    },

    addGift: function (offer) {
      return this._cartRequest('/cart/add.js', { id: offer.freeVariantId, quantity: 1, properties: { _auto_added: 'true' } }, offer);
    },

    removeGift: function (offer, lineKey) {
      return this._cartRequest('/cart/change.js', { id: lineKey, quantity: 0 }, offer);
    },

    dismissKey: function (offer) { return 'auto-add-dismissed-' + offer.offerId; },

    isDismissed: function (offer) {
      try { return sessionStorage.getItem(this.dismissKey(offer)) === '1'; }
      catch (e) { return false; }
    },

    setDismissed: function (offer, value) {
      try {
        if (value) sessionStorage.setItem(this.dismissKey(offer), '1');
        else sessionStorage.removeItem(this.dismissKey(offer));
      } catch (e) { /* sessionStorage unavailable; degrade gracefully */ }
    },

    syncReminders: function (cart) {
      var self = this;
      var containers = document.querySelectorAll('.auto-add-reminder');
      containers.forEach(function (el) {
        var offerId = el.getAttribute('data-offer-id');
        var offer = self.offers.filter(function (o) { return o.offerId === offerId; })[0];
        if (!offer) { el.hidden = true; return; }

        var giftPresent = !!self.getGiftLine(offer, cart);
        var eligible = self.isEligible(offer, cart);
        var present = el.querySelector('.auto-add-reminder__present');
        var removed = el.querySelector('.auto-add-reminder__removed');
        if (!present || !removed) { el.hidden = true; return; }

        if (giftPresent) {
          el.hidden = false; present.hidden = false; removed.hidden = true;
        } else if (eligible && offer.available && self.isDismissed(offer)) {
          el.hidden = false; present.hidden = true; removed.hidden = false;
        } else {
          el.hidden = true; present.hidden = true; removed.hidden = true;
        }
      });
    },

    wireReminderButtons: function () {
      var self = this;
      document.addEventListener('click', function (e) {
        var btn = e.target.closest ? e.target.closest('.auto-add-reminder__add') : null;
        if (!btn) return;
        var container = btn.closest('.auto-add-reminder');
        if (!container) return;
        var offerId = container.getAttribute('data-offer-id');
        var offer = self.offers.filter(function (o) { return o.offerId === offerId; })[0];
        if (!offer) return;
        btn.setAttribute('disabled', 'disabled');
        self.setDismissed(offer, false);
        self.addGift(offer);
      });
    },

    // Shared add/change request that re-renders the cart UI and re-broadcasts cart state.
    _cartRequest: function (url, payload, offer) {
      var self = this;
      this.inFlight[offer.offerId] = true;
      var sections = this.sectionsToRequest();
      payload.sections = sections.map(function (s) { return s.section || s.id; });
      payload.sections_url = window.location.pathname;
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res && res.sections) self.applySections(res.sections, sections);
          return self.fetchCart();
        })
        .then(function (cart) {
          // Keep cart.js (<cart-items>) in step: it shows "You can only add N of this item" when a
          // change leaves its remembered item count unchanged, and it doesn't see gift adds/removals.
          var cartItems = document.querySelector('cart-items');
          if (cart && cartItems && 'currentItemCount' in cartItems) cartItems.currentItemCount = cart.item_count;
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        })
        .catch(function (err) { console.error('AutoAddOffer cart request failed', url, err); })
        .then(function () { self.inFlight[offer.offerId] = false; });
    },

    // Build the Section Rendering API list for whatever cart UI is on the page.
    // Mirrors this theme's assets/cart.js getSectionsToRender(); cross-check there if
    // the cart UI does not refresh after an engine change.
    sectionsToRequest: function () {
      var list = [];
      // On cart_type:notification themes, cart-notification's getSectionsToRender() returns
      // id-keyed entries (no .section), so some become no-op request keys that applySections
      // harmlessly drops. The load-bearing entries are main-cart-items and cart-icon-bubble below.
      var drawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        drawer.getSectionsToRender().forEach(function (s) { list.push(s); });
      }
      var itemsEl = document.getElementById('main-cart-items');
      if (itemsEl) list.push({ id: 'main-cart-items', section: itemsEl.dataset.id, selector: '.js-contents' });
      var footerEl = document.getElementById('main-cart-footer');
      if (footerEl) list.push({ id: 'main-cart-footer', section: footerEl.dataset.id, selector: '.js-contents' });
      var upsellEl = document.getElementById('cart-upsell');
      if (upsellEl && footerEl) list.push({ id: 'cart-upsell', section: footerEl.dataset.id, selector: '.cart__collection' });
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble) list.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
      return list;
    },

    // Replace the DOM for each requested section from the rendered HTML in the response.
    applySections: function (rendered, list) {
      list.forEach(function (s) {
        var key = s.section || s.id;
        var html = rendered[key];
        var el = document.getElementById(s.id);
        if (!html || !el) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = s.selector ? doc.querySelector(s.selector) : doc.body;
        var target = (s.selector && el.querySelector(s.selector)) || el;
        if (node) target.innerHTML = node.innerHTML;
      });
    },

    evaluate: function (cart) {
      var self = this;
      this.offers.forEach(function (offer) {
        if (self.inFlight[offer.offerId]) return;

        var eligible = self.isEligible(offer, cart);
        var giftPresent = !!self.getGiftLine(offer, cart);
        var wasPresent = self._lastGiftPresent[offer.offerId] === true;

        // Customer manually removed the gift while still eligible -> respect it.
        if (wasPresent && !giftPresent && eligible) {
          self.setDismissed(offer, true);
        }

        if (eligible && !giftPresent && offer.available && !self.isDismissed(offer)) {
          self.addGift(offer);
          return;
        }

        var autoLine = self.getAutoGiftLine(offer, cart);
        if (!eligible && autoLine) {
          // Engine-initiated removal of our own auto-added line only; do NOT
          // set the dismiss flag, and never touch a manually-added line of the
          // same variant (rule 6).
          self.removeGift(offer, autoLine.key);
          self._lastGiftPresent[offer.offerId] = false;
          return;
        }

        // Becoming ineligible clears a stale dismiss so a future re-qualification starts clean.
        if (!eligible && self.isDismissed(offer)) {
          self.setDismissed(offer, false);
        }

        self._lastGiftPresent[offer.offerId] = giftPresent;
      });
      this.syncReminders(cart);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { window.AutoAddOffer.init(); });
  } else {
    window.AutoAddOffer.init();
  }
})();
